import { Scale, Gavel, Truck, Car, HeartPulse, Footprints } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Seo from "@/components/Seo";
import { RESULTS_STATS, CAREER_TIMELINE, IMAGES } from "@/lib/site";
import { FinalCTA } from "./Home";

const EXPERIENCE_BLOCKS = [
  {
    icon: HeartPulse,
    title: "Personal Injury Trials",
    body: "Decades of personal injury litigation — from negotiation through jury verdict — holding insurers and defendants accountable.",
  },
  {
    icon: Car,
    title: "Motor Vehicle Accidents",
    body: "Car, motorcycle, and pedestrian collision claims pursued through settlement or trial when insurers refuse fair value.",
  },
  {
    icon: Truck,
    title: "Truck Accidents",
    body: "Complex commercial-vehicle crash litigation involving federal regulations and multiple liable parties.",
  },
  {
    icon: Footprints,
    title: "Slip & Fall / Premises",
    body: "Premises liability claims against negligent property owners for unsafe conditions and hidden hazards.",
  },
  {
    icon: Gavel,
    title: "Criminal Defense Trials",
    body: "Jury and bench trial defense across felony and misdemeanor charges — informed by years on the prosecution side.",
  },
  {
    icon: Scale,
    title: "Federal & State Courts",
    body: "Trial and appellate practice from Massachusetts courts to the First Circuit and the U.S. Supreme Court.",
  },
];

export default function Results() {
  return (
    <div data-testid="results-page" className="bg-white">
      <Seo
        title="Results & Experience | Mahaney Law"
        description="40+ years of trial and appellate experience across Massachusetts and federal courts — personal injury and criminal defense."
        path="/results"
      />

      {/* Header — centered image-forward hero with horizontal stat strip */}
      <section data-testid="results-hero" className="relative overflow-hidden bg-navy-950 md:flex md:flex-col md:justify-center md:min-h-[calc(100vh-6rem)]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.55]"
          style={{ backgroundImage: `url(${IMAGES.hero_results})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/55 via-navy-950/45 to-navy-950/80" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-16 pt-20 md:pt-10 pb-0 text-center">
          <Reveal>
            <div className="eyebrow text-gold">Results & Experience</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.75rem] leading-[1.05] tracking-tight text-white mt-5 text-balance">
              40+ years.
              <span className="block italic text-gold">
                Federal & state courtrooms.
              </span>
            </h1>
            <p className="mt-7 mx-auto max-w-2xl text-lg text-white/70 leading-relaxed font-light">
              Attorney Mahaney's experience spans hundreds of cases, dozens of
              trials, and decades of advocacy across Massachusetts and federal
              courts.
            </p>
          </Reveal>
        </div>
        {/* Stat strip */}
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 lg:px-16 mt-14 md:mt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-b border-white/10 divide-x divide-white/10">
            {RESULTS_STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="py-8 px-4 text-center">
                  <div className="font-display text-4xl md:text-5xl text-gold leading-none">
                    {s.value}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-white/55 font-semibold leading-snug">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="h-16 md:h-10" />
      </section>

      {/* Experience blocks */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Areas of Practice"
            title="Trial-ready in every venue."
            body="A practice built across the spectrum of Massachusetts and federal courts."
            testId="experience-blocks"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCE_BLOCKS.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`experience-block-${i}`}
                  className="rounded-sm border border-navy-900/10 bg-white p-7 h-full hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_30px_60px_-30px_rgba(10,25,47,0.25)] transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-sm bg-gold/10 grid place-items-center">
                    <b.icon className="h-5 w-5 text-gold" />
                  </div>
                  <h3 className="font-display text-2xl text-navy-900 mt-6 leading-tight">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-navy-900/65 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image strip */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { src: IMAGES.courthouse, label: "Federal & State Courtrooms" },
              { src: IMAGES.justice_scales, label: "Trial Practice" },
            ].map((img, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="relative aspect-[16/10] rounded-sm overflow-hidden bg-navy-900 border border-navy-900/10">
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="eyebrow text-gold">Experience</div>
                    <div className="font-display text-2xl mt-1">{img.label}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Career timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Career Highlights"
            title="A trajectory built around the courtroom."
            testId="career-highlights"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {CAREER_TIMELINE.map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`career-card-${i}`}
                  className="rounded-sm border border-navy-900/10 bg-navy-50 p-7 h-full"
                >
                  <div className="eyebrow text-gold">{t.year}</div>
                  <h3 className="font-display text-2xl text-navy-900 mt-3 leading-tight">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-navy-900/65 leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
